import { Box, easeOutQuad, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { COLORS } from '@lemonade-hq/tokens';
import { clampedInversedLerp, lerp } from '@lemonade-hq/ts-helpers';
import styled, { keyframes } from 'styled-components';

const Container = styled(Box)`
  overflow: hidden;
  border-radius: 100%;
  will-change: transform, contents;
`;

const PetriDish = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${COLORS.glass};
  border-radius: 100%;
  position: relative;
  overflow: hidden;
  will-change: transform;
`;

const scaryAnimation = keyframes`
    0% { transform: translate(-2px, 2px); }
    10% { transform: translate(-5px, -4px); }
    20% { transform: translate(-1px, -6px); }
    25% { transform: translate(6px, 3px); }
    40% { transform: translate(3px, 6px); }
    50% { transform: translate(2px, 5px); }
    60% { transform: translate(3px, 6px); }
    70% { transform: translate(0px, 3px); }
    80% { transform: translate(4px, -3px); }
    90% { transform: translate(3px, -5px); }
    100% { transform: translate(0px, 0px); }
`;

const Virus = styled.div`
  background-color: ${themedColor('white')};
  border-radius: 100%;
  position: absolute;
  animation: ${scaryAnimation} 3s infinite;

  ${MEDIA_QUERIES.upToDesktop`
        width: 14px;
        height: 14px;
    `}

  ${MEDIA_QUERIES.desktop`
        width: 26px;
        height: 26px;
    `};
`;

const Corona = styled(Box)`
  background-color: ${themedColor('primary')};
  border-radius: 100%;
  position: absolute;

  ${MEDIA_QUERIES.upToDesktop`
        transform: translate(-50%, -50%);
    `};

  ${MEDIA_QUERIES.desktop`
        transform: translate(-24.7px, -24.7px);
    `};
`;

const CrownedCorona = styled.div`
  border-radius: 100%;
  position: absolute;

  ${MEDIA_QUERIES.upToDesktop`
        width: 8px;
        height: 8px;
        margin-top: -3.5px;
        margin-left: -4px;
        box-shadow: -27px 0 ${themedColor('primary')}, 27px 0 ${themedColor(
          'primary',
        )}, 0 27px ${themedColor('primary')}, 0 -27px ${themedColor('primary')}, -19.14px -19.14px ${themedColor(
          'primary',
        )}, -19.14px 19.14px ${themedColor('primary')}, 19.14px 19.14px ${themedColor(
          'primary',
        )}, 19.14px -19.14px ${themedColor('primary')};
    `}

  ${MEDIA_QUERIES.desktop`
        width: 11px;
        height: 11px;
        box-shadow: -40px 0 ${themedColor('primary')}, 40px 0 ${themedColor(
          'primary',
        )}, 0 40px ${themedColor('primary')}, 0 -40px ${themedColor(
          'primary',
        )}, -28px -28px ${themedColor('primary')}, -28px 28px ${themedColor(
          'primary',
        )}, 28px 28px ${themedColor('primary')}, 28px -28px ${themedColor('primary')};
    `};
`;

const AnimatedCrown = styled.div`
  position: absolute;
  border-radius: 100%;
  animation: ${scaryAnimation} 3s infinite;

  ${MEDIA_QUERIES.upToDesktop`
        width: 5px;
        height: 5px;
        box-shadow: -12px 0 white, 12px 0 white, 0 12px white, 0 -12px white, -8.51px -8.51px white, -8.51px 8.51px white, 8.51px 8.51px white, 8.51px -8.51px white;
    `}

  ${MEDIA_QUERIES.desktop`
        width: 8px;
        height: 8px;
        box-shadow: -25px 0 white, 25px 0 white, 0 25px white, 0 -25px white, -17.8px -17.8px white, -17.8px 17.8px white, 17.8px 17.8px white, 17.8px -17.8px white;
    `};
`;

export default function Giveback2020Covid({ t, ...props }) {
  const appearT = easeOutQuad(clampedInversedLerp(0, 0.5, t));
  const coronaT = clampedInversedLerp(0.5, 1, t);
  const opacity = lerp(0, 1, appearT);
  const coronaRotation = coronaT * 250;
  const coronaX = lerp(0, 100, coronaT);
  const coronaY = lerp(100, 0, coronaT);
  const display = t === 0 ? 'none' : 'block';

  return (
    <Container {...props}>
      <PetriDish style={{ opacity }}>
        <Virus style={{ left: '17%', top: '35%', display }} />
        <AnimatedCrown style={{ left: '19.1%', top: '37%', display }} />
        <Virus
          style={{
            left: '50%',
            top: '10%',
            animationDelay: '-10000ms',
            display,
          }}
        />
        <AnimatedCrown
          style={{
            left: '52.2%',
            top: '12%',
            animationDelay: '-10000ms',
            display,
          }}
        />
        <Virus
          style={{
            left: '55%',
            top: '85%',
            animationDelay: '-2000ms',
            display,
          }}
        />
        <AnimatedCrown
          style={{
            left: '57.2%',
            top: '87%',
            animationDelay: '-2000ms',
            display,
          }}
        />
        <Virus
          style={{
            left: '80.5%',
            top: '40%',
            animationDelay: '-2500ms',
            display,
          }}
        />
        <AnimatedCrown
          style={{
            left: '82.4%',
            top: '42.2%',
            animationDelay: '-2500ms',
            display,
          }}
        />
        <Corona
          width={[40, 40, 60]}
          height={[40, 40, 60]}
          style={{
            left: `${coronaY}%`,
            top: `${coronaX}%`,
          }}
        />
        <CrownedCorona
          style={{
            left: `${coronaY}%`,
            top: `${coronaX}%`,
            transform: `rotate(${coronaRotation}deg)`,
          }}
        />
        {t >= 0.875 && (
          <>
            <Corona
              width={[40, 40, 60]}
              height={[40, 40, 60]}
              style={{
                left: '25%',
                top: '75%',
                opacity: 0.2,
                backgroundColor: COLORS.pink,
              }}
            />
          </>
        )}
      </PetriDish>
    </Container>
  );
}
